import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportArea,
  ReportPreset,
  STOCK_MOVEMENT_CATEGORIES,
  type InventoryReportQuery,
  type StockMovementCategory,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useInventoryReportQuery } from '../api/reports.api';
import { InventoryContent } from '../components/InventoryContent';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';

/** The filters this page keeps in the URL, so a reading can be shared or reloaded unchanged. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  const movement = params.get('movement');
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: 'none' as const,
    } satisfies ReportFilters,
    productId: params.get('product') ?? '',
    variantId: params.get('variant') ?? '',
    categoryId: params.get('category') ?? '',
    brandId: params.get('brand') ?? '',
    movementCategory: (STOCK_MOVEMENT_CATEGORIES.includes(movement as StockMovementCategory)
      ? movement
      : '') as StockMovementCategory | '',
  };
}

/**
 * Reports › Inventory.
 *
 * Current stock is always as of now; the period only narrows movements, transfers, counts and
 * adjustments, so the filter bar offers no comparison. Filters are ids in the URL and are resolved —
 * and refused — server-side.
 */
export function InventoryReportPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.INVENTORY);
  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: InventoryReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    branchId: period.branchId || undefined,
    productId: filters.productId || undefined,
    variantId: filters.variantId || undefined,
    categoryId: filters.categoryId || undefined,
    brandId: filters.brandId || undefined,
    movementCategory: filters.movementCategory || undefined,
  };
  const report = useInventoryReportQuery(query, { enabled: customReady && allowed });
  const applied = report.data?.filters ?? null;

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    if (merged.productId) search.set('product', merged.productId);
    if (merged.variantId) search.set('variant', merged.variantId);
    if (merged.categoryId) search.set('category', merged.categoryId);
    if (merged.brandId) search.set('brand', merged.brandId);
    if (merged.movementCategory) search.set('movement', merged.movementCategory);
    setParams(search, { replace: true });
  };

  const field = 'border-line bg-surface text-ink w-full rounded-lg border px-3 py-2 text-sm';
  const option = (rows: { id: string | null; label: string | null }[]) =>
    rows
      .filter((row): row is { id: string; label: string | null } => row.id !== null)
      .map((row) => (
        <option key={row.id} value={row.id}>
          {row.label ?? row.id}
        </option>
      ));

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.inventory.title')} subtitle={t('reports.inventory.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.inventory.noAccess')}</Alert>}
      {allowed ? (
        <>
          <ReportFilterBar
            value={period}
            onChange={(next) => write({ period: { ...next, compare: 'none' } })}
            hideCompare
          />
          <Card className="p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label
                  htmlFor="inventory-category"
                  className="text-muted mb-1 block text-xs font-medium"
                >
                  {t('reports.sales.filters.category')}
                </label>
                <select
                  id="inventory-category"
                  className={field}
                  value={filters.categoryId}
                  onChange={(event) => write({ categoryId: event.target.value })}
                >
                  <option value="">{t('reports.sales.filters.allCategories')}</option>
                  {option(report.data?.byCategory ?? [])}
                </select>
              </div>
              <div>
                <label
                  htmlFor="inventory-brand"
                  className="text-muted mb-1 block text-xs font-medium"
                >
                  {t('reports.sales.filters.brand')}
                </label>
                <select
                  id="inventory-brand"
                  className={field}
                  value={filters.brandId}
                  onChange={(event) => write({ brandId: event.target.value })}
                >
                  <option value="">{t('reports.sales.filters.allBrands')}</option>
                  {option(report.data?.byBrand ?? [])}
                </select>
              </div>
              <div>
                <label
                  htmlFor="inventory-movement"
                  className="text-muted mb-1 block text-xs font-medium"
                >
                  {t('reports.inventory.filters.movement')}
                </label>
                <select
                  id="inventory-movement"
                  className={field}
                  value={filters.movementCategory}
                  onChange={(event) =>
                    write({ movementCategory: event.target.value as StockMovementCategory | '' })
                  }
                >
                  <option value="">{t('reports.inventory.filters.allMovements')}</option>
                  {STOCK_MOVEMENT_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {t(`reports.inventory.movement.${category}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* What the server says it resolved: a product or item arrives from a drilldown row or a link, never typed. */}
            {applied && (applied.productName || applied.variantSku) ? (
              <p className="text-muted mt-3 text-sm">
                {t('reports.inventory.filters.item', {
                  names: [applied.productName, applied.variantSku].filter(Boolean).join(' · '),
                })}
              </p>
            ) : null}
            {filters.productId ||
            filters.variantId ||
            filters.categoryId ||
            filters.brandId ||
            filters.movementCategory ? (
              <button
                type="button"
                className="text-brand-700 mt-3 text-sm font-medium hover:underline"
                onClick={() =>
                  write({
                    productId: '',
                    variantId: '',
                    categoryId: '',
                    brandId: '',
                    movementCategory: '',
                  })
                }
              >
                {t('reports.sales.filters.clear')}
              </button>
            ) : null}
          </Card>
        </>
      ) : null}
      {report.data ? <ReportMetaLine meta={report.data.meta} /> : null}
      {!customReady && allowed ? (
        <Alert tone="info">{t('reports.filters.chooseDates')}</Alert>
      ) : null}
      {report.isError ? (
        <Alert tone="error">
          {apiErrorCode(report.error) === 'VALIDATION_FAILED'
            ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
            : t('reports.loadError')}
        </Alert>
      ) : null}
      {report.isPending && customReady && allowed ? (
        <div className="space-y-4" aria-hidden>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-surface border-line h-28 animate-pulse rounded-xl border"
              />
            ))}
          </div>
        </div>
      ) : null}
      {report.data ? (
        <InventoryContent
          report={report.data}
          query={query}
          onSelectVariant={(variantId) => write({ productId: '', variantId })}
        />
      ) : null}
    </div>
  );
}
